/**
 * Retry status indicator component
 * Shows retry progress and countdown for 503 error handling
 */

import { Clock, AlertCircle, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

interface RetryStatus {
  isRetrying: boolean;
  attempt: number;
  maxAttempts: number;
  nextRetryIn: number;
  remainingSeconds?: number;
  totalSeconds?: number;
  error: string;
  delayMs: number;
}

interface RetryStatusIndicatorProps {
  retryStatus: RetryStatus;
  className?: string;
}

export function RetryStatusIndicator({ retryStatus, className = '' }: RetryStatusIndicatorProps) {
  const {
    attempt,
    maxAttempts,
    remainingSeconds = 0,
    totalSeconds = 0,
    error
  } = retryStatus;

  // Calculate progress percentage for countdown
  const countdownProgress = totalSeconds > 0 
    ? ((totalSeconds - remainingSeconds) / totalSeconds) * 100 
    : 0;

  return (
    <div className={`flex flex-col gap-2 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg ${className}`}>
      {/* Header with retry info */}
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 text-orange-600 animate-spin" />
        <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
          Retrying due to server overload
        </span>
        <Badge variant="outline" className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
          Attempt {attempt}/{maxAttempts}
        </Badge>
      </div>

      {/* Error message */}
      <div className="flex items-center gap-2 text-sm text-orange-700 dark:text-orange-300">
        <AlertCircle className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">{error}</span>
      </div>

      {/* Countdown section */}
      {remainingSeconds > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-orange-600" />
              <span className="text-orange-700 dark:text-orange-300">
                Next attempt in {remainingSeconds}s
              </span>
            </div>
            <span className="text-xs text-orange-600 dark:text-orange-400">
              {Math.round(countdownProgress)}%
            </span>
          </div>
          
          <Progress 
            value={countdownProgress} 
            className="h-2 bg-orange-200 dark:bg-orange-900/50"
          />
        </div>
      )}

      {/* Strategy info */}
      <div className="text-xs text-orange-600 dark:text-orange-400">
        Using smart retry strategy: {attempt <= 6 ? 'linear backoff' : attempt <= 16 ? 'exponential backoff' : 'final attempts'}
      </div>
    </div>
  );
}